import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useParams } from "react-router";
import {
  QuoteContextType,
  SaveQuoteReqType,
  QuoteStateType,
  PickedQuoteLineType,
} from "../types/quote/quoteResults.type";
import { QuoteLine } from "../types/quote";
import { queryClient, transformAddress } from "../utils";
import { QuotesType } from "../types";
import { useSaveQuoteData } from "../api/mutation";
import { useGetQuoteById, useGetMultiSiteResults } from "../api/queries";
import { useModal } from "./Modal.context";
import GoToPageButton from "@/components/ActionsButtons/GoToPageButton";
import StayInPageButton from "@/components/ActionsButtons/StayInPageButton";
import { InfoModal } from "@/components/Modals";
import { usePermissions } from "./Permissions.context";
import { useTableContext } from "./Table.context";

const QuoteContext = createContext<QuoteContextType | null>(null);

const QuoteProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const params = useParams();
  const { filters, currentPage, searchQ } = useTableContext();
  const [total, setTotal] = useState<number>(0);
  const { company } = usePermissions();
  const [pickedQuotes, setPickedQuotes] = useState<SaveQuoteReqType[]>([]);
  const [quoteState, setQuoteState] = useState<QuoteStateType[]>();
  const { data: quoteData } = useGetQuoteById(
    params.quoteResultId || "0",
    filters,
    searchQ,
    currentPage * 10
  );
  const { data: multiQuoteData } = useGetMultiSiteResults(
    params.multiQuoteId || "0",
    filters,
    searchQ
  );
  const { mutate: saveQuote } = useSaveQuoteData();
  const { openModal } = useModal();

  const setPickedQuote = ({
    pikedQuoteId,
    pickedQuoteLineId,
  }: {
    pikedQuoteId: number;
    pickedQuoteLineId: PickedQuoteLineType;
  }) => {
    setPickedQuotes((prevState) => {
      const existingQuote = prevState.find(
        (el) => el.quoteReqId === pikedQuoteId
      );

      if (existingQuote) {
        const existingQuoteLine = existingQuote.quoteLines.find(
          (el) => el.quoteLineId === pickedQuoteLineId.quoteLineId
        );
        if (existingQuoteLine) {
          return prevState.map((el) =>
            el.quoteReqId === pikedQuoteId
              ? {
                  ...el,
                  quoteLines: el.quoteLines.filter(
                    (el) => el.quoteLineId !== pickedQuoteLineId.quoteLineId
                  ),
                }
              : el
          );
        }
        return prevState.map((el) =>
          el.quoteReqId === pikedQuoteId
            ? {
                ...el,
                quoteLines: Array.from(
                  new Set([...el.quoteLines, pickedQuoteLineId])
                ),
              }
            : el
        );
      }

      return [
        ...prevState,
        { quoteReqId: pikedQuoteId, quoteLines: [pickedQuoteLineId] },
      ];
    });
  };
  const createQuote = () => {
    saveQuote(pickedQuotes, {
      onSuccess: () => {
        queryClient.invalidateQueries();
        openModal(
          <InfoModal
            instruction={{
              behavior: "success",
              description: "",
              header: "Quote created",
            }}
            actions={[
              <StayInPageButton />,
              <GoToPageButton
                destinationName="Go to Quotes"
                link={
                  company.companyType === "customer"
                    ? "/customer/quotes/me"
                    : "/quotes"
                }
              />,
            ]}
          />
        );
      },
    });
  };

  useEffect(() => {
    if (location.pathname.includes("multiSiteResults") && multiQuoteData) {
      const quotes: QuoteStateType[] = multiQuoteData.items.map((el: any) => {
        return {
          pickedAAddress: transformAddress(el.addressA),
          productType: el.type,
          generalQuoteData: {
            quoteId: el.id,
            created: el.createdAt,
            customerProjectNumber: el.customerProjectNumber,
            dueDate: el.dueDate,
            endCustomerName: el.endCustomerName,
            RFSdate: el.RFSdate,
            productType: el.type,
            companyId: el.initiatorId,
            isHaveResults: el.isHaveResults,
          },
          pickedBAddress: el.addressB
            ? transformAddress(el.addressB)
            : undefined,
          quoteLines: el.quoteLines,
        };
      });
      setPickedQuotes([]);
      return setQuoteState(quotes);
    }

    if (location.pathname.includes("singleSiteQuoteResults") && quoteData) {
      const quotes: QuoteStateType[] = [
        {
          pickedAAddress: transformAddress(quoteData.quoteReq.addressA),
          pickedBAddress: quoteData.quoteReq.addressB
            ? transformAddress(quoteData.quoteReq.addressB)
            : undefined,
          generalQuoteData: {
            created: quoteData.quoteReq.createdAt,
            customerProjectNumber: quoteData.quoteReq.customerProjectNumber,
            dueDate: quoteData.quoteReq.dueDate,
            endCustomerName: quoteData.quoteReq.endCustomerName,
            productType: quoteData.quoteReq.type as QuotesType,
            quoteId: quoteData.quoteReq.id,
            RFSdate: quoteData.quoteReq.RFSdate,
            companyId: quoteData.quoteReq.initiatorId,
          },
          productType: quoteData.quoteReq.type as QuotesType,
          quoteLines: quoteData.items as QuoteLine[],
        },
      ];
      setTotal(quoteData.count);
      setPickedQuotes([]);
      return setQuoteState(quotes);
    }
  }, [quoteData, multiQuoteData]);
  return (
    <QuoteContext.Provider
      value={{
        quoteState,
        createQuote,
        setPickedQuote,
        pickedQuotes,
        total,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};
export const useQuote = (): QuoteContextType => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error("useQuote must be used within a QuoteProvider");
  }
  return context;
};
export default QuoteProvider;
